// File: src/components/Staff/StaffInsightsTable.jsx

import React, { useEffect, useMemo, useState } from "react";
import api from "@/utils/axios";
import ensureArray from "@/utils/ensureArray";
import {
  ArrowRight,
  BriefcaseBusiness,
  Clock3,
  ShieldAlert,
  Wallet,
} from "lucide-react";

const toNum = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const isObj = (v) => v && typeof v === "object" && !Array.isArray(v);

const pickArray = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.rows)) return raw.rows;
  if (Array.isArray(raw?.data)) return raw.data;
  if (Array.isArray(raw?.items)) return raw.items;
  if (Array.isArray(raw?.staff)) return raw.staff;
  return [];
};

const money = (n) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(toNum(n, 0));

const formatLastClock = (value) => {
  if (!value) return "—";

  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString("en-GB");
  } catch {
    return String(value);
  }
};

const normalizeRows = (raw) => {
  const list = ensureArray(pickArray(raw));

  return list.map((staff, index) => ({
    id:
      staff?.id ??
      staff?.staff_id ??
      staff?.employee_id ??
      index,

    name:
      staff?.name ??
      staff?.full_name ??
      "-",

    role:
      staff?.role ??
      staff?.position ??
      "-",

    department:
      staff?.department ??
      staff?.department_name ??
      "-",

    hours_this_week: toNum(
      staff?.hours_this_week ??
        staff?.hoursThisWeek ??
        staff?.weekly_hours ??
        staff?.total_hours,
      0
    ),

    total_pay: toNum(
      staff?.total_pay ??
        staff?.totalPay ??
        staff?.gross_pay ??
        staff?.pay,
      0
    ),

    leave_used: toNum(
      staff?.leave_used ??
        staff?.leaveUsed ??
        staff?.total_leave_used,
      0
    ),

    last_clock:
      staff?.last_clock ??
      staff?.lastClock ??
      staff?.last_clock_in ??
      null,

    flag: String(
      staff?.flag ??
        staff?.status ??
        "unknown"
    ).toLowerCase(),
  }));
};

const flagTone = (flag) => {
  if (flag === "ok") {
    return "bg-emerald-100 text-emerald-800 border-emerald-200";
  }

  if (flag === "overworked") {
    return "bg-amber-100 text-amber-800 border-amber-200";
  }

  return "bg-rose-100 text-rose-800 border-rose-200";
};

const StaffInsightsTable = ({
  onClick,
  data = null,
  loading = false,
  error = "",
  source = null,
  refreshKey = 0,
}) => {
  const [rows, setRows] = useState([]);
  const [localLoading, setLocalLoading] = useState(true);
  const [localError, setLocalError] = useState("");

  const hasParentData = useMemo(() => {
    return isObj(data) || Array.isArray(data);
  }, [data]);

  useEffect(() => {
    let alive = true;

    if (hasParentData) {
      const normalized = normalizeRows(data);

      if (alive) {
        setRows(normalized);
        setLocalError("");
        setLocalLoading(false);
      }

      return () => {
        alive = false;
      };
    }

    setLocalLoading(true);

    (async () => {
      try {
        const res = await api.get("/solo-mdsh-api/staffing/insights");
        const normalized = normalizeRows(res?.data);

        if (alive) {
          setRows(normalized);
          setLocalError("");
        }
      } catch (e) {
        console.error("Error fetching staff insights:", e);

        if (alive) {
          setRows([]);
          setLocalError("Could not load staff insights right now.");
        }
      } finally {
        if (alive) setLocalLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [hasParentData, data, refreshKey]);

  const effectiveLoading = loading && !hasParentData ? true : localLoading;
  const effectiveError = error || localError;

  const summary = useMemo(() => {
    const overworked = rows.filter((r) => r.flag === "overworked").length;
    const risk = rows.filter((r) => r.flag !== "ok").length;
    const totalPay = rows.reduce((acc, row) => acc + toNum(row.total_pay, 0), 0);

    return {
      totalPeople: rows.length,
      overworked,
      risk,
      totalPay,
    };
  }, [rows]);

  const headline = useMemo(() => {
    if (effectiveLoading) return "Loading staff insights...";
    if (effectiveError) return "Staff insight data could not be fully loaded.";
    if (rows.length === 0) return "No staff insight rows were returned.";
    if (summary.overworked > 0) return "Some staff members may need workload review.";
    if (summary.risk > 0) return "There are staff signals that need attention.";
    return "Staff activity looks stable from the current data.";
  }, [effectiveLoading, effectiveError, rows.length, summary.overworked, summary.risk]);

  const topRows = rows.slice(0, 6);
  const clickable = typeof onClick === "function";

  return (
    <div
      onClick={clickable ? onClick : undefined}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      className={`rounded-3xl border border-slate-200 bg-gradient-to-br from-white via-slate-50 to-indigo-50 p-6 shadow-sm transition ${
        clickable ? "cursor-pointer hover:-translate-y-0.5 hover:shadow-md" : ""
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-2xl font-bold text-slate-900">Staff Insights</h3>
          <p className="mt-1 text-sm text-slate-600">
            A quick view of staffing hours, pay, and possible people risks.
          </p>
        </div>

        <div className="flex flex-col items-end gap-2">
          {clickable ? (
            <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-sky-700">
              Open dashboard
              <ArrowRight size={14} />
            </div>
          ) : null}

          {source ? (
            <div className="text-[11px] text-slate-500">Source: {source}</div>
          ) : null}
        </div>
      </div>

      {effectiveError ? (
        <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs font-medium text-amber-800">
          {effectiveError}
        </div>
      ) : null}

      <div className="mt-4 rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm font-medium text-sky-900">
        {headline}
      </div>

      {effectiveLoading ? (
        <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-5 text-sm text-slate-600">
          Loading staff insights...
        </div>
      ) : rows.length === 0 ? (
        <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-5 text-sm text-slate-600">
          No staff insights to show yet.
        </div>
      ) : (
        <>
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="rounded-2xl border border-sky-200 bg-sky-50 p-4">
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-semibold text-sky-800">People Tracked</div>
                <div className="rounded-xl bg-white/70 p-2">
                  <BriefcaseBusiness size={18} className="text-sky-700" />
                </div>
              </div>
              <div className="mt-3 text-3xl font-bold text-slate-900">{summary.totalPeople}</div>
            </div>

            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-semibold text-amber-800">Overworked Flags</div>
                <div className="rounded-xl bg-white/70 p-2">
                  <ShieldAlert size={18} className="text-amber-700" />
                </div>
              </div>
              <div className="mt-3 text-3xl font-bold text-slate-900">{summary.overworked}</div>
            </div>

            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-semibold text-emerald-800">Total Pay</div>
                <div className="rounded-xl bg-white/70 p-2">
                  <Wallet size={18} className="text-emerald-700" />
                </div>
              </div>
              <div className="mt-3 text-2xl font-bold text-slate-900">{money(summary.totalPay)}</div>
            </div>
          </div>

          <div className="mt-5 overflow-x-auto rounded-2xl border border-slate-200 bg-white">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="text-slate-600">
                  <th className="px-4 py-3 text-left font-semibold">Name</th>
                  <th className="px-4 py-3 text-left font-semibold">Role</th>
                  <th className="px-4 py-3 text-left font-semibold">Department</th>
                  <th className="px-4 py-3 text-center font-semibold">Hours</th>
                  <th className="px-4 py-3 text-right font-semibold">Pay</th>
                  <th className="px-4 py-3 text-center font-semibold">Leave</th>
                  <th className="px-4 py-3 text-left font-semibold">Last Clock</th>
                  <th className="px-4 py-3 text-center font-semibold">Status</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-200">
                {topRows.map((staff) => (
                  <tr key={staff.id} className="hover:bg-slate-50 transition">
                    <td className="px-4 py-3 font-medium text-slate-900">{staff.name}</td>
                    <td className="px-4 py-3 text-slate-700">{staff.role}</td>
                    <td className="px-4 py-3 text-slate-700">{staff.department}</td>
                    <td className="px-4 py-3 text-center text-slate-700">
                      {staff.hours_this_week}h
                    </td>
                    <td className="px-4 py-3 text-right text-slate-900 font-semibold">
                      {money(staff.total_pay)}
                    </td>
                    <td className="px-4 py-3 text-center text-slate-700">
                      {staff.leave_used}
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      <div className="inline-flex items-center gap-2">
                        <Clock3 size={14} className="text-slate-400" />
                        <span>{formatLastClock(staff.last_clock)}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span
                        className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wide ${flagTone(
                          staff.flag
                        )}`}
                      >
                        {staff.flag}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {rows.length > topRows.length ? (
            <div className="mt-3 text-xs text-slate-500">
              Showing {topRows.length} of {rows.length} staff insight rows.
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

export default StaffInsightsTable;